import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/providers/auth-provider";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { getCollectionDetails } from "@/services/collection/get-collection-details";
import { GetCollectionDetailsOutput } from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RouteNames } from "@/types/route-names";
import { Alert } from "@/components/alert/alert";
import { AddIcon } from "@/icons";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import cn from "classnames";
import { Button } from "@/components/ui/buttons/button";

export const CollectionDetailsView = () => {
  const { getAccessToken } = useAuth();
  const { setCurrentRoute, setIntent, registerBackAction } = useSPAContext();
  const [data, setData] = useState<GetCollectionDetailsOutput | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const collectionId = window.localStorage.getItem("selected_collection_id");

  useEffect(() => {
    if (!collectionId) {
      setCurrentRoute(RouteNames.COLLECTION_LIST);
      return;
    }

    registerBackAction(() => {
      setCurrentRoute(RouteNames.INVENTORY_POKE);
    });

    const fetchDetails = async () => {
      const token = getAccessToken();
      if (!token) return;

      setIsLoading(true);
      try {
        const response = await getCollectionDetails(token, {
          collection_id: collectionId,
        });
        if (response.ok) {
          setData(response.data);
        }
      } catch (error) {
        console.error(error);
        Alert("Failed to fetch collection details");
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [collectionId]);

  // Map of base_pokemon_name -> pokemon data
  const obtainedMap = useMemo(() => {
    if (!data) return new Map();
    const map = new Map<string, (typeof data.obtained_pokemons)[0]>();
    data.obtained_pokemons.forEach((p) => map.set(p.base_pokemon_name, p));
    return map;
  }, [data]);

  const fillableMap = useMemo(() => {
    if (!data) return new Set();
    return new Set(data.available_species);
  }, [data]);

  const progressRatio = useMemo(() => {
    if (!data) return 0;
    return data.obtained_pokemons.length / data.collection.slots.length;
  }, [data]);

  const handleAddPokemonToSlot = (slotName: string) => {
    window.localStorage.setItem("selected_collection_slot", slotName);
    setIntent(Intent.SELECTING_POKEMON_FOR_COLLECTION);
    setCurrentRoute(RouteNames.INVENTORY_POKE);
  };

  return (
    <div className="w-full h-full p-1 flex flex-col gap-1 bg-black/70">
      <StandardFrame variant="night" className="flex flex-col gap-4 h-full">
        <div className="flex flex-col gap-2 border-b border-white/10 pb-3">
          <div className="flex items-center gap-3">
            {data?.collection.region && (
              <img
                src={`/assets/icons/books/${data.collection.region.toLowerCase()}.png`}
                className="w-12 h-12 object-contain"
                alt={data.collection.region}
              />
            )}
            <h2 className="text-xl font-bold text-white uppercase tracking-wider truncate flex-1">
              {data?.collection.display_name || "Collection"}
            </h2>
            <Button
              variant="yellow"
              disabled
              className="opacity-50 grayscale cursor-not-allowed"
            >
              <span className="px-2 font-bold uppercase text-xs">Rewards</span>
            </Button>
          </div>

          <div className="flex flex-col gap-1 px-1">
            <div className="flex justify-between items-end">
              <span className="text-[10px] font-bold text-white/50 uppercase tracking-tighter">
                Collection Progress
              </span>
              <span className="text-[10px] font-bold text-white">
                {data?.obtained_pokemons.length || 0} /{" "}
                {data?.collection.slots.length || 0}
              </span>
            </div>
            <SegmentedProgressBar
              ratio={progressRatio}
              variant="green"
              maxSquares={20}
              className="h-4 border-2"
              segmented={false}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-3 xs:grid-cols-4 gap-2 p-1 content-start">
          {isLoading ? (
            <div className="col-span-full text-center text-white/50">
              Loading...
            </div>
          ) : !data ? (
            <div className="col-span-full text-center text-white/50">
              Details not found.
            </div>
          ) : (
            data.collection.slots.map((slotName, index) => {
              const pokemon = obtainedMap.get(slotName);
              const isObtained = !!pokemon;
              const isFillable = !isObtained && fillableMap.has(slotName);

              return (
                <div
                  key={index}
                  className={cn(
                    "h-18  rounded border flex flex-col items-center justify-center relative transition-all overflow-hidden",
                    {
                      "bg-pk-purple-1 border-pk-green-4 shadow-[0_0_5px_rgba(82,189,139,0.5)]":
                        isObtained,
                      "bg-pk-blue-5/20 border-pk-blue-4 shadow-[0_0_8px_rgba(59,130,246,0.6)] animate-pulse cursor-pointer":
                        isFillable,
                      "bg-black/30 border-white/10 opacity-50 grayscale":
                        !isObtained && !isFillable,
                    },
                  )}
                  title={slotName}
                  onClick={
                    isFillable
                      ? () => handleAddPokemonToSlot(slotName)
                      : undefined
                  }
                >
                  {/* Number in collection */}
                  <div className="absolute top-0.5 left-1 z-20">
                    <div
                      className={cn(
                        "text-[10px] font-bold px-1 rounded-sm shadow-cartoon-sm",
                        isObtained
                          ? "bg-pk-green-4 text-white"
                          : "text-white/30",
                      )}
                    >
                      #{index + 1}
                    </div>
                  </div>

                  {/* Sprite or placeholder */}
                  {isObtained ? (
                    <div className="flex-1 flex items-center justify-center pt-2">
                      <PokemonSprite
                        pokemon={pokemon}
                        style={{ scale: 1.5 }}
                        showShadow
                      />
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-[10px] text-center break-all p-1 capitalize leading-tight">
                        {slotName}
                      </div>
                    </div>
                  )}

                  {/* Bottom Bar for obtained */}
                  {isObtained && (
                    <div className="w-full px-1 mb-1 mt-auto">
                      <div className="bg-pk-purple-3 pt-1 w-full flex items-center justify-center rounded-sm">
                        <StrokedText
                          fontSize={10}
                          strokeWidth={4}
                          letterSpacing={1}
                        >
                          {slotName.toUpperCase()}
                        </StrokedText>
                      </div>
                    </div>
                  )}

                  {/* Add Button if not obtained */}
                  {!isObtained && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddPokemonToSlot(slotName);
                      }}
                      className={cn(
                        "absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity z-10",
                        isFillable
                          ? "opacity-100"
                          : "opacity-0 hover:opacity-100",
                      )}
                    >
                      <AddIcon
                        width={isFillable ? 24 : 32}
                        height={isFillable ? 24 : 32}
                        fill="white"
                      />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </StandardFrame>
    </div>
  );
};
